import React, { FC, ReactNode } from 'react';
import MuiTypography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material';

export type Variant = 'primary' | 'secondary';
export type Tag = 'p' | 'h6' | 'h5' | 'h4' | 'h3' | 'h2' | 'h1' | 'span';
export type Size =
  | 'xs'
  | 's'
  | 'm'
  | 'l'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl';
export type Alignment = 'left' | 'right' | 'center' | 'justify';
export type FontWeight = 'light' | 'normal' | 'semi-bold' | 'bold';
export type TypographyCase = 'none' | 'lowercase' | 'uppercase' | 'capitalize';

export interface TypographyProps {
  alignment?: Alignment;
  fontWeight?: FontWeight;
  italicized?: boolean;
  keepDefaultMargins?: boolean;
  textCase?: TypographyCase;
  className?: string;
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

const SIZE_STYLES: Record<Size, { fontSize: string; lineHeight: string }> = {
  xs: { fontSize: '0.75rem', lineHeight: '1rem' },
  s: { fontSize: '0.875rem', lineHeight: '1.25rem' },
  m: { fontSize: '1rem', lineHeight: '1.5rem' },
  l: { fontSize: '1.125rem', lineHeight: '1.5rem' },
  xl: { fontSize: '1.25rem', lineHeight: '1.75rem' },
  '2xl': { fontSize: '1.5rem', lineHeight: '2rem' },
  '3xl': { fontSize: '1.875rem', lineHeight: '2.25rem' },
  '4xl': { fontSize: '2.25rem', lineHeight: '2.5rem' },
  '5xl': { fontSize: '2.5rem', lineHeight: '3rem' },
  '6xl': { fontSize: '3rem', lineHeight: '3.5rem' },
  '7xl': { fontSize: '3.75rem', lineHeight: '3.75rem' },
};

const FONT_WEIGHT_MAP: Record<FontWeight, number> = {
  light: 300,
  normal: 400,
  'semi-bold': 600,
  bold: 700,
};

interface TypographyArgs extends TypographyProps {
  tag: Tag;
  variant: Variant;
  size: Size;
}

const RenderMuiTypography = ({
  tag,
  variant,
  size,
  alignment = 'left',
  fontWeight = 'normal',
  italicized = false,
  keepDefaultMargins = false,
  textCase = 'none',
  className,
  children,
  sx,
}: TypographyArgs) => {
  const sizeStyle = SIZE_STYLES[size] || SIZE_STYLES.m;

  return (
    <MuiTypography
      component={tag}
      className={className}
      sx={{
        fontSize: sizeStyle.fontSize,
        lineHeight: sizeStyle.lineHeight,
        textAlign: alignment,
        fontWeight: FONT_WEIGHT_MAP[fontWeight] || 400,
        fontStyle: italicized ? 'italic' : 'normal',
        textTransform: textCase !== 'none' ? textCase : 'none',
        color: variant === 'secondary' ? 'text.secondary' : 'text.primary',
        fontFamily: 'inherit',
        m: keepDefaultMargins ? undefined : 0,
        ...sx,
      }}
    >
      {children}
    </MuiTypography>
  );
};

export const PrimaryTypography = {
  Medium: (props: TypographyProps) => (
    <RenderMuiTypography tag="h6" variant="primary" size="m" {...props} />
  ),
  Large: (props: TypographyProps) => (
    <RenderMuiTypography tag="h6" variant="primary" size="l" {...props} />
  ),
  XLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h5" variant="primary" size="xl" {...props} />
  ),
  TwoXLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h4" variant="primary" size="2xl" {...props} />
  ),
  ThreeXLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h4" variant="primary" size="3xl" {...props} />
  ),
  FourXLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h4" variant="primary" size="4xl" {...props} />
  ),
  FiveXLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h3" variant="primary" size="5xl" {...props} />
  ),
  SixXLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h2" variant="primary" size="6xl" {...props} />
  ),
  SevenXLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="h1" variant="primary" size="7xl" {...props} />
  ),
};

export const SecondaryTypography = {
  XSmall: (props: TypographyProps) => (
    <RenderMuiTypography tag="p" variant="secondary" size="xs" {...props} />
  ),
  Small: (props: TypographyProps) => (
    <RenderMuiTypography tag="p" variant="secondary" size="s" {...props} />
  ),
  Medium: (props: TypographyProps) => (
    <RenderMuiTypography tag="p" variant="secondary" size="m" {...props} />
  ),
  Large: (props: TypographyProps) => (
    <RenderMuiTypography tag="p" variant="secondary" size="l" {...props} />
  ),
  XLarge: (props: TypographyProps) => (
    <RenderMuiTypography tag="p" variant="secondary" size="xl" {...props} />
  ),
};

export default PrimaryTypography;
